import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  MinusCircleIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';
import type {
  AdminUser,
  FileOpError,
  FileOpErrorSeverity,
  JobType,
  Locale,
  ReconciliationExtensionsResult,
  ScanCounts,
  ScanReconciliation,
  ScanSkipReason,
} from '@xphoto/shared';
import { FILE_OP_ERRORS_DEFAULT_LIMIT, SCAN_SKIP_REASONS } from '@xphoto/shared';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n';
import { interpolate } from '../gallery/format';
import { formatDateTime } from '../viewer/format';
import {
  fetchFileErrors,
  fetchReconciliationExtensions,
  fetchScanReconciliation,
  purgeFileErrors,
  skippedExportUrl,
  type FileErrorsFilters,
} from './file-errors-api';
import { withSlotParam } from '../lib/activeSlot';
import { fetchUsers } from './admin-api';
import { DynamicFilterSelect, FilterSelect, type FilterOption } from './FilterSelect';
import { PurgeByDateControl } from './PurgeByDateControl';

/**
 * Section « Erreurs de fichiers » (Lot C) — écran de consultation du JOURNAL DES
 * ERREURS DE TRAITEMENT DE FICHIERS (Lot A/B). Modèle de bout en bout :
 * `RequestLogsSection`.
 *
 * - Barre de 3 FILTRES : Utilisateur / Statut (sévérité, DÉFAUT = Erreurs) /
 *   Méthode (type de job, groupé : EXIF = publish+erase, Dédup = dedup+apply).
 *   Changer un filtre recharge la page 0. Filtres composables.
 * - Tableau responsive (icône + texte pour la sévérité — jamais la couleur seule).
 * - Pagination précédent/suivant. États chargement / erreur (réessai) / vide.
 * - Purge unifiée via `PurgeByDateControl` (accès écriture requis : le composant
 *   est masqué si le compte n'a pas le droit d'écrire — grantee lecture seule).
 */

type LoadStatus = 'idle' | 'loading' | 'error' | 'ready';

const PAGE_SIZE = FILE_OP_ERRORS_DEFAULT_LIMIT;

/** Groupes du filtre « Méthode » (type de job). Une valeur d'UI ↦ 1..N `JobType`. */
type MethodGroup = 'scan' | 'move' | 'exif' | 'dedup' | 'trash';

const METHOD_GROUP_JOB_TYPES: Record<MethodGroup, readonly JobType[]> = {
  scan: ['scan', 'thumbnail'],
  move: ['move'],
  exif: ['exif_publish', 'exif_erase', 'originals_cleanup'],
  dedup: ['dedup', 'dedup_apply', 'dedup_exact_apply'],
  trash: ['trash_purge'],
};

const METHOD_OPTIONS: readonly FilterOption<MethodGroup>[] = [
  { value: '', labelKey: 'admin.fileErrors.methodAll' },
  { value: 'scan', labelKey: 'admin.fileErrors.methodScan' },
  { value: 'move', labelKey: 'admin.fileErrors.methodMove' },
  { value: 'exif', labelKey: 'admin.fileErrors.methodExif' },
  { value: 'dedup', labelKey: 'admin.fileErrors.methodDedup' },
  { value: 'trash', labelKey: 'admin.fileErrors.methodTrash' },
];

const SEVERITY_OPTIONS: readonly FilterOption<FileOpErrorSeverity>[] = [
  { value: '', labelKey: 'admin.fileErrors.statusAll' },
  { value: 'error', labelKey: 'admin.fileErrors.statusError' },
  { value: 'skipped', labelKey: 'admin.fileErrors.statusSkipped' },
  { value: 'warning', labelKey: 'admin.fileErrors.statusWarning' },
];

export function FileErrorsSection({
  libraryId,
  canWrite,
}: {
  libraryId: string | null;
  canWrite: boolean;
}): React.JSX.Element {
  const { t, locale } = useI18n();

  // Filtres (défaut Statut = « error » : on ouvre sur les vraies pannes).
  const [userId, setUserId] = useState('');
  const [severity, setSeverity] = useState<FileOpErrorSeverity | ''>('error');
  const [method, setMethod] = useState<MethodGroup | ''>('');

  const [status, setStatus] = useState<LoadStatus>('idle');
  const [errors, setErrors] = useState<FileOpError[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [users, setUsers] = useState<AdminUser[]>([]);

  useEffect(() => {
    if (libraryId === null) {
      setStatus('idle');
      return;
    }
    // Compose les filtres pour l'API (le groupe « Méthode » → liste de jobTypes).
    const filters: FileErrorsFilters = {
      ...(userId !== '' ? { userId } : {}),
      ...(severity !== '' ? { severity } : {}),
      ...(method !== '' ? { jobTypes: METHOD_GROUP_JOB_TYPES[method] } : {}),
    };
    const controller = new AbortController();
    setStatus('loading');
    fetchFileErrors(libraryId, PAGE_SIZE, offset, filters, controller.signal)
      .then((page) => {
        if (controller.signal.aborted) return;
        setErrors(page.errors);
        setTotal(page.total);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setStatus('error');
        void err;
      });
    return () => controller.abort();
  }, [libraryId, offset, reloadKey, userId, severity, method]);

  // Alimente le filtre « Utilisateur » (liste des comptes, best-effort).
  useEffect(() => {
    const controller = new AbortController();
    fetchUsers(controller.signal)
      .then((list) => {
        if (!controller.signal.aborted) setUsers(list);
      })
      .catch(() => {
        // Non bloquant : on retombe sur « Tous » seul.
      });
    return () => controller.abort();
  }, []);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // Changer un filtre repart de la page 0.
  const onFilterChange =
    <T,>(setter: (v: T) => void) =>
    (v: T): void => {
      setter(v);
      setOffset(0);
    };

  const purge = useCallback(
    async (beforeIso: string): Promise<number> => {
      if (libraryId === null) return 0;
      return purgeFileErrors(libraryId, beforeIso);
    },
    [libraryId],
  );

  const onPurged = useCallback(() => {
    setOffset(0);
    reload();
  }, [reload]);

  const rangeFrom = total === 0 ? 0 : offset + 1;
  const rangeTo = Math.min(offset + errors.length, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  const userOptions = useMemo(
    () => users.map((u) => ({ value: u.id, label: `${u.displayName} — ${u.email}` })),
    [users],
  );

  return (
    <section
      id="admin-file-errors"
      role="tabpanel"
      aria-labelledby="admin-file-errors-heading"
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-1">
        <h3
          id="admin-file-errors-heading"
          className="flex items-center gap-2 text-base font-semibold text-fg"
        >
          <ExclamationCircleIcon className="h-5 w-5" aria-hidden="true" />
          {t('admin.fileErrors.title')}
        </h3>
        <p className="text-sm text-muted">{t('admin.fileErrors.intro')}</p>
      </div>

      {libraryId === null ? (
        <p className="py-6 text-center text-muted">{t('admin.noLibrarySelected')}</p>
      ) : (
        <>
          {/* Encart de RÉCONCILIATION du dernier scan (fichiers disque ↔ galerie). */}
          <ScanReconciliationCard libraryId={libraryId} locale={locale} />

          {/* Barre de 3 filtres : Utilisateur / Statut / Méthode. */}
          <div className="flex flex-wrap items-end gap-3">
            <DynamicFilterSelect
              id="fe-filter-user"
              labelKey="admin.fileErrors.filterUser"
              allLabelKey="admin.fileErrors.userAll"
              value={userId}
              options={userOptions}
              onChange={onFilterChange(setUserId)}
            />
            <FilterSelect<FileOpErrorSeverity>
              id="fe-filter-severity"
              labelKey="admin.fileErrors.filterStatus"
              value={severity}
              options={SEVERITY_OPTIONS}
              onChange={onFilterChange<FileOpErrorSeverity | ''>(setSeverity)}
            />
            <FilterSelect<MethodGroup>
              id="fe-filter-method"
              labelKey="admin.fileErrors.filterMethod"
              value={method}
              options={METHOD_OPTIONS}
              onChange={onFilterChange<MethodGroup | ''>(setMethod)}
            />
          </div>

          {/* Purge unifiée (accès écriture requis). */}
          {canWrite && <PurgeByDateControl onPurge={purge} onPurged={onPurged} />}

          {status === 'error' ? (
            <FileErrorsLoadError onRetry={reload} />
          ) : status === 'ready' ? (
            <>
              <FileErrorsTable errors={errors} locale={locale} />
              {total > 0 && (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-muted" role="status">
                    {interpolate(
                      t('admin.fileErrors.range'),
                      {
                        from: rangeFrom,
                        to: rangeTo,
                        total,
                      },
                      locale,
                    )}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={!canPrev}
                      onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                      className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                    >
                      {t('admin.fileErrors.previous')}
                    </button>
                    <button
                      type="button"
                      disabled={!canNext}
                      onClick={() => setOffset((o) => o + PAGE_SIZE)}
                      className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                    >
                      {t('admin.fileErrors.next')}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p role="status" className="py-8 text-center text-muted">
              {t('admin.loading')}
            </p>
          )}
        </>
      )}
    </section>
  );
}

/** Libellé i18n de chaque RAISON d'exclusion (ventilation « ignorés »). */
const SKIP_REASON_LABEL: Record<ScanSkipReason, MessageKey> = {
  unsupported_extension: 'admin.fileErrors.reconReasonUnsupported',
  non_media: 'admin.fileErrors.reconReasonNonMedia',
  unreadable: 'admin.fileErrors.reconReasonUnreadable',
  decode_error: 'admin.fileErrors.reconReasonDecode',
  mismatched_type: 'admin.fileErrors.reconReasonMismatched',
};

/**
 * Encart de RÉCONCILIATION du dernier scan terminé. Répond à « pourquoi X fichiers
 * sur disque mais Y en galerie ? » : X = Y indexés + Z copies exactes fusionnées +
 * W ignorés [détail par raison]. Factuel, icône + texte (jamais la couleur seule).
 * Silencieux tant qu'aucun scan n'a abouti (ou en cas d'échec de chargement).
 */
function ScanReconciliationCard({
  libraryId,
  locale,
}: {
  libraryId: string;
  locale: Locale;
}): React.JSX.Element | null {
  const { t } = useI18n();
  const [recon, setRecon] = useState<ScanReconciliation | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setFailed(false);
    fetchScanReconciliation(libraryId, controller.signal)
      .then((r) => {
        if (!controller.signal.aborted) setRecon(r);
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setFailed(true);
        void err;
      });
    return () => controller.abort();
  }, [libraryId]);

  // Pas encore de scan abouti (ou chargement en échec) → rien à afficher.
  if (failed || recon === null || recon.counts === null) return null;

  const counts: ScanCounts = recon.counts;
  const when =
    recon.finishedAt !== null
      ? (formatDateTime(recon.finishedAt, locale) ?? recon.finishedAt)
      : null;
  // Écart total non expliqué (devrait être 0 si l'invariant tient) — affiché en note.
  const accounted = counts.indexed + counts.exactCollapsed + counts.skipped;
  const unaccounted = counts.filesWalked - accounted;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface-2 p-4">
      <h4 className="flex items-center gap-2 text-sm font-semibold text-fg">
        <ScaleIcon className="h-5 w-5" aria-hidden="true" />
        {t('admin.fileErrors.reconTitle')}
      </h4>

      {/* Phrase de synthèse : disque = indexés + copies exactes + ignorés. */}
      <p className="text-sm text-fg">
        {interpolate(t('admin.fileErrors.reconSummary'), {
          walked: counts.filesWalked,
          indexed: counts.indexed,
          collapsed: counts.exactCollapsed,
          skipped: counts.skipped,
        })}
      </p>
      {when !== null && (
        <p className="text-xs text-muted">
          {interpolate(
            t(recon.dryRun ? 'admin.fileErrors.reconWhenDry' : 'admin.fileErrors.reconWhen'),
            {
              when,
            },
          )}
        </p>
      )}

      {/* Ventilation chiffrée (liste de définitions accessible). */}
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <ReconRow label={t('admin.fileErrors.reconWalked')} value={counts.filesWalked} strong />
        <ReconRow label={t('admin.fileErrors.reconIndexed')} value={counts.indexed} />
        <ReconRow label={t('admin.fileErrors.reconCollapsed')} value={counts.exactCollapsed} />
        <ReconRow label={t('admin.fileErrors.reconSkipped')} value={counts.skipped} />
      </dl>

      {counts.skipped > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-muted">{t('admin.fileErrors.reconByReason')}</p>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            {SCAN_SKIP_REASONS.filter((r) => counts.skippedByReason[r] > 0).map((r) => (
              <ReconRow key={r} label={t(SKIP_REASON_LABEL[r])} value={counts.skippedByReason[r]} />
            ))}
          </dl>
          {/* Détail des extensions refusées (sous « Extension non prise en charge »). */}
          {counts.skippedByReason.unsupported_extension > 0 && (
            <UnsupportedExtensionsBreakdown libraryId={libraryId} />
          )}
        </div>
      )}

      {unaccounted !== 0 && (
        <p className="flex items-center gap-1 text-xs text-warning-text">
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {interpolate(t('admin.fileErrors.reconUnaccounted'), { count: unaccounted }, locale)}
        </p>
      )}

      {/* Téléchargements (uniquement s'il y a des fichiers ignorés). */}
      {counts.skipped > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-2 border-t border-border/50 pt-3">
          <a
            download
            href={withSlotParam(skippedExportUrl(libraryId, 'all'))}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <ArrowDownTrayIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('admin.fileErrors.reconDownloadSkipped')}
          </a>
          <a
            download
            href={withSlotParam(skippedExportUrl(libraryId, 'non_media'))}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <ArrowDownTrayIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('admin.fileErrors.reconDownloadNonMedia')}
          </a>
        </div>
      )}
    </div>
  );
}

/**
 * Ventilation des extensions REFUSÉES (`unsupported_extension`) du dernier scan
 * (ex. `.webm — 180`), fetchée à part de l'endpoint dédié. Compacte, défilable si
 * longue. Icône + texte. Silencieuse tant qu'aucune donnée / en cas d'échec.
 */
function UnsupportedExtensionsBreakdown({
  libraryId,
}: {
  libraryId: string;
}): React.JSX.Element | null {
  const { t } = useI18n();
  const [result, setResult] = useState<ReconciliationExtensionsResult | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchReconciliationExtensions(libraryId, controller.signal)
      .then((r) => {
        if (!controller.signal.aborted) setResult(r);
      })
      .catch(() => {
        // Non bloquant : la ventilation par extension est un plus.
      });
    return () => controller.abort();
  }, [libraryId]);

  if (result === null || result.extensions.length === 0) return null;

  return (
    <div className="flex flex-col gap-1 rounded-md bg-surface p-2">
      <p className="text-xs font-medium text-muted">{t('admin.fileErrors.reconExtensionsTitle')}</p>
      <ul className="max-h-40 overflow-y-auto text-sm">
        {result.extensions.map((e) => (
          <li key={e.ext} className="flex items-center justify-between gap-3 py-0.5">
            <span className="text-fg">
              {e.ext === '' ? t('admin.fileErrors.reconExtensionNone') : e.ext}
            </span>
            <span className="tabular-nums text-muted">{e.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Une ligne « intitulé … valeur » de la ventilation de réconciliation. */
function ReconRow({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: number;
  strong?: boolean;
}): React.JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/50 py-0.5">
      <dt className={strong ? 'font-medium text-fg' : 'text-muted'}>{label}</dt>
      <dd className={`tabular-nums ${strong ? 'font-semibold text-fg' : 'text-fg'}`}>{value}</dd>
    </div>
  );
}

/** Libellé i18n du type de job (méthode) affiché dans la table. */
const JOB_TYPE_LABEL: Record<JobType, MessageKey> = {
  scan: 'admin.fileErrors.jobScan',
  thumbnail: 'admin.fileErrors.jobThumbnail',
  ai_tag: 'admin.fileErrors.jobAiTag',
  dedup: 'admin.fileErrors.jobDedup',
  dedup_apply: 'admin.fileErrors.jobDedupApply',
  dedup_exact_apply: 'admin.fileErrors.jobDedupExactApply',
  exif_publish: 'admin.fileErrors.jobExifPublish',
  exif_erase: 'admin.fileErrors.jobExifErase',
  move: 'admin.fileErrors.jobMove',
  compress: 'admin.fileErrors.jobCompress',
  rotate: 'admin.fileErrors.jobRotate',
  originals_cleanup: 'admin.fileErrors.jobOriginalsCleanup',
  trash_purge: 'admin.fileErrors.jobTrashPurge',
  rename_extensions: 'admin.fileErrors.jobRenameExtensions',
  empty_folders_cleanup: 'admin.fileErrors.jobEmptyFoldersCleanup',
};

/** Icône + libellé de sévérité (jamais la couleur seule). */
function SeverityBadge({ severity }: { severity: FileOpErrorSeverity }): React.JSX.Element {
  const { t } = useI18n();
  let icon: React.ReactNode;
  let labelKey: MessageKey;
  if (severity === 'error') {
    icon = <ExclamationCircleIcon className="h-4 w-4 shrink-0 text-danger" aria-hidden="true" />;
    labelKey = 'admin.fileErrors.statusError';
  } else if (severity === 'warning') {
    icon = (
      <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-warning-text" aria-hidden="true" />
    );
    labelKey = 'admin.fileErrors.statusWarning';
  } else {
    icon = <MinusCircleIcon className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />;
    labelKey = 'admin.fileErrors.statusSkipped';
  }
  return (
    <span className="inline-flex items-center gap-1 text-fg">
      {icon}
      <span className="font-medium">{t(labelKey)}</span>
    </span>
  );
}

function FileErrorsTable({
  errors,
  locale,
}: {
  errors: readonly FileOpError[];
  locale: Locale;
}): React.JSX.Element {
  const { t } = useI18n();
  if (errors.length === 0) {
    return <p className="py-6 text-center text-muted">{t('admin.fileErrors.empty')}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[52rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-start text-xs font-medium text-muted">
            <th scope="col" className="p-2 text-start">
              {t('admin.fileErrors.colTimestamp')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.fileErrors.colSeverity')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.fileErrors.colMethod')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.fileErrors.colUser')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.fileErrors.colPath')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.fileErrors.colMime')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.fileErrors.colMessage')}
            </th>
          </tr>
        </thead>
        <tbody>
          {errors.map((e) => (
            <FileErrorRow key={e.id} error={e} locale={locale} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FileErrorRow({
  error,
  locale,
}: {
  error: FileOpError;
  locale: Locale;
}): React.JSX.Element {
  const { t } = useI18n();
  const when = formatDateTime(error.ts, locale) ?? error.ts;
  return (
    <tr className="border-b border-border">
      <td className="p-2 align-top text-muted whitespace-nowrap">{when}</td>
      <td className="p-2 align-top">
        <SeverityBadge severity={error.severity} />
      </td>
      <td className="p-2 align-top text-fg font-medium whitespace-nowrap">
        {t(JOB_TYPE_LABEL[error.jobType])}
      </td>
      <td className="p-2 align-top text-muted">
        <span className="break-all">{error.userEmail ?? t('admin.fileErrors.noValue')}</span>
      </td>
      <td className="p-2 align-top text-fg">
        <span className="break-all">{error.relPath ?? t('admin.fileErrors.noValue')}</span>
      </td>
      <td className="p-2 align-top text-muted">
        <span className="break-all">{error.detectedMime ?? t('admin.fileErrors.noValue')}</span>
      </td>
      <td className="p-2 align-top text-fg">
        <span className="break-all">{error.message}</span>
        {error.errorCode !== null && (
          <span className="ml-1 rounded bg-surface-2 px-1 text-xs text-muted">
            {error.errorCode}
          </span>
        )}
      </td>
    </tr>
  );
}

/** État d'erreur de chargement (icône + texte + réessai). */
function FileErrorsLoadError({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <ExclamationTriangleIcon className="h-8 w-8 text-danger" aria-hidden="true" />
      <p className="text-fg">{t('admin.errorTitle')}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {t('admin.retry')}
      </button>
    </div>
  );
}
